import React, { useState } from 'react';
import {
  AlertCircle,
  Save,
  X,
  Info,
  CheckSquare,
  Square,
  Edit2,
  Plus,
  Minus
} from 'lucide-react';
import toast from 'react-hot-toast';
import { US_STATES } from '../../lib/constants';

interface BusinessAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface KeyPerson {
  name: string;
  title: string;
  email: string;
  phone?: string;
  linkedinUrl?: string;
  resumeUrl?: string;
}

interface PastPerformanceRecord {
  projectName: string;
  sectorType: 'Government' | 'Private Sector';
  agency?: string;
  contractValue: number;
  contractType: 'Prime' | 'Subcontractor' | 'JV';
  deliverables: string;
  documentUrl?: string;
}

interface ComplianceDocument {
  documentType: string;
  documentUrl: string;
  expirationDate?: string;
  issuingAuthority?: string;
  issueDate?: string;
}

export interface ExtractedData {
  // Company Information
  companyName?: string;
  legalStructure?: string;
  yearEstablished?: number;
  dunsNumber?: string;
  ueiNumber?: string;
  cageCode?: string;
  samStatus?: string;
  email?: string;
  phone?: string;
  website?: string;
  businessAddress?: BusinessAddress;

  // Capabilities
  coreCompetencies?: string[];
  industriesServed?: string[];
  keyTechnologies?: string[];
  valueProposition?: string;
  naicsCodes?: string[];
  setAsides?: string[];

  // Key Personnel
  keyPersonnel?: KeyPerson[];

  // Past Performance
  pastPerformance?: PastPerformanceRecord[];

  // Compliance Documents
  complianceDocuments?: ComplianceDocument[];

  // Free-form Narrative
  missionStatement?: string;
  executiveSummary?: string;
  
  // Metadata
  confidence: { [key: string]: number };
  warnings: string[];
}

interface DocumentReviewFormProps {
  extractedData: ExtractedData;
  onSave: (data: Partial<ExtractedData>) => void;
  onCancel: () => void;
}

const fieldLabels: { [K in keyof Omit<ExtractedData, "confidence" | "warnings">]?: { label: string; description: string } } = {
  companyName: { label: "Company Name", description: "Legal business name as registered" },
  legalStructure: { label: "Legal Structure", description: "Business entity type (e.g., LLC, Corporation)" },
  yearEstablished: { label: "Year Established", description: "Year the company was founded" },
  dunsNumber: { label: "DUNS Number", description: "9-digit Dun & Bradstreet identification number" },
  ueiNumber: { label: "UEI", description: "12-character Unique Entity Identifier from SAM.gov" },
  cageCode: { label: "CAGE Code", description: "5-character Commercial and Government Entity code" },
  samStatus: { label: "SAM.gov Status", description: "Current registration status in SAM.gov" },
  email: { label: "Email Address", description: "Primary business contact email" },
  phone: { label: "Phone Number", description: "Primary business contact phone" },
  website: { label: "Website", description: "Company website URL" },
  businessAddress: { label: "Business Address", description: "Primary business location" },
  coreCompetencies: { label: "Core Competencies", description: "Primary business capabilities and strengths" },
  industriesServed: { label: "Industries Served", description: "Target industries and markets" },
  keyTechnologies: { label: "Key Technologies", description: "Primary technologies and tools used" },
  valueProposition: { label: "Value Proposition", description: "Unique value offered to customers" },
  naicsCodes: { label: "NAICS Codes", description: "North American Industry Classification System codes" },
  setAsides: { label: "Set Asides", description: "Small business program qualifications" },
  keyPersonnel: { label: "Key Personnel", description: "Key staff members and leadership" },
  pastPerformance: { label: "Past Performance", description: "Previous contract experience" },
  complianceDocuments: { label: "Compliance Documents", description: "Required certifications and documentation" },
  missionStatement: { label: "Mission Statement", description: "Company's purpose and goals" },
  executiveSummary: { label: "Executive Summary", description: "Brief overview of the company" }
};

export default function DocumentReviewForm({ extractedData, onSave, onCancel }: DocumentReviewFormProps) {
  const [formData, setFormData] = useState<ExtractedData>(extractedData);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(Object.keys(extractedData).filter(key => extractedData[key as keyof ExtractedData] !== undefined))
  );
  const [editingField, setEditingField] = useState<string | null>(null);

  const sections: { [section: string]: (keyof Omit<ExtractedData, "confidence" | "warnings">)[] } = {
    "Company Information": [
      'companyName', 'legalStructure', 'yearEstablished', 'dunsNumber', 'ueiNumber',
      'cageCode', 'samStatus', 'email', 'phone', 'website', 'businessAddress'
    ],
    "Capabilities": [
      'coreCompetencies', 'industriesServed', 'keyTechnologies',
      'valueProposition', 'naicsCodes', 'setAsides'
    ],
    "Key Personnel": ['keyPersonnel'],
    "Past Performance": ['pastPerformance'],
    "Compliance": ['complianceDocuments'],
    "Narrative": ['missionStatement', 'executiveSummary']
  };

  const getConfidenceColor = (score: number): string => {
    if (score >= 0.9) return 'text-success bg-success-bg';
    if (score >= 0.7) return 'text-fit-moderate bg-fit-moderate-bg';
    return 'text-urgent bg-urgent-bg';
  };

  const toggleSectionSelection = (sectionKeys: (keyof ExtractedData)[]) => {
    const allSelected = sectionKeys.every(key => selectedFields.has(key as string));
    const newSelected = new Set(selectedFields);
    sectionKeys.forEach(key => {
      if (allSelected) {
        newSelected.delete(key as string);
      } else {
        newSelected.add(key as string);
      }
    });
    setSelectedFields(newSelected);
  };

  const handleArrayItemChange = (field: keyof ExtractedData, index: number, value: string) => {
    const newArray = [...(formData[field] as string[])];
    newArray[index] = value;
    handleFieldChange(field, newArray);
  };

  const handleAddArrayItem = (field: keyof ExtractedData) => {
    const newArray = [...(formData[field] as string[]), ''];
    handleFieldChange(field, newArray);
  };

  const handleRemoveArrayItem = (field: keyof ExtractedData, index: number) => {
    const newArray = (formData[field] as string[]).filter((_, i) => i !== index);
    handleFieldChange(field, newArray);
  };

  const handleFieldChange = (field: keyof ExtractedData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderConfidence = (field: keyof ExtractedData) => {
    const score = extractedData.confidence[field as string];
    if (score !== undefined) {
      const colorClass = getConfidenceColor(score);
      return (
        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${colorClass}`}>
          {Math.round(score * 100)}% confidence
        </span>
      );
    }
    return null;
  };

  const renderBusinessAddress = (addr: BusinessAddress) => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-bw-ink">
            Street Address 1
          </label>
          <input
            type="text"
            value={addr.street1 || ''}
            onChange={(e) => handleFieldChange('businessAddress', { ...addr, street1: e.target.value })}
            className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
            placeholder="123 Main Street"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-bw-ink">
            Street Address 2
          </label>
          <input
            type="text"
            value={addr.street2 || ''}
            onChange={(e) => handleFieldChange('businessAddress', { ...addr, street2: e.target.value })}
            className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
            placeholder="Suite 100"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-bw-ink">
              City
            </label>
            <input
              type="text"
              value={addr.city || ''}
              onChange={(e) => handleFieldChange('businessAddress', { ...addr, city: e.target.value })}
              className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-bw-ink">
              State
            </label>
            <select
              value={addr.state || ''}
              onChange={(e) => handleFieldChange('businessAddress', { ...addr, state: e.target.value })}
              className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
            >
              <option value="">Select state</option>
              {Object.entries(US_STATES).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-bw-ink">
              ZIP Code
            </label>
            <input
              type="text"
              value={addr.zip || ''}
              onChange={(e) => handleFieldChange('businessAddress', { ...addr, zip: e.target.value })}
              className="mt-1 block w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              placeholder="12345"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-bw-ink">
            Country
          </label>
          <input
            type="text"
            value={addr.country || 'United States'}
            readOnly
            className="mt-1 block w-full rounded-md border-bw-border bg-bw-surface-subtle shadow-sm text-bw-muted"
          />
        </div>
      </div>
    );
  };

  const renderEditableValue = (key: keyof ExtractedData, value: any) => {
    // Special handling for business address
    if (key === 'businessAddress' && value) {
      return renderBusinessAddress(value as BusinessAddress);
    }

    // Handle keyPersonnel
    if (key === 'keyPersonnel' && Array.isArray(value)) {
      return (
        <div className="space-y-4">
          {value.map((person, index) => (
            <div key={index} className="border p-4 rounded-lg">
              <div className="mb-2 font-semibold">Person {index + 1}</div>
              <label className="text-sm font-medium">Full Name *</label>
              <input
                type="text"
                value={person.name}
                onChange={(e) => {
                  const newPersonnel = [...(formData.keyPersonnel || [])];
                  newPersonnel[index] = { ...person, name: e.target.value };
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary mb-2"
              />
              <label className="text-sm font-medium">Title/Position *</label>
              <input
                type="text"
                value={person.title}
                onChange={(e) => {
                  const newPersonnel = [...(formData.keyPersonnel || [])];
                  newPersonnel[index] = { ...person, title: e.target.value };
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary mb-2"
              />
              <label className="text-sm font-medium">Email Address *</label>
              <input
                type="email"
                value={person.email}
                onChange={(e) => {
                  const newPersonnel = [...(formData.keyPersonnel || [])];
                  newPersonnel[index] = { ...person, email: e.target.value };
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary mb-2"
              />
              <label className="text-sm font-medium">Phone Number</label>
              <input
                type="tel"
                value={person.phone || ''}
                onChange={(e) => {
                  const newPersonnel = [...(formData.keyPersonnel || [])];
                  newPersonnel[index] = { ...person, phone: e.target.value };
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary mb-2"
              />
              <label className="text-sm font-medium">LinkedIn Profile</label>
              <input
                type="url"
                value={person.linkedinUrl || ''}
                onChange={(e) => {
                  const newPersonnel = [...(formData.keyPersonnel || [])];
                  newPersonnel[index] = { ...person, linkedinUrl: e.target.value };
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                placeholder="https://linkedin.com/in/username"
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary mb-2"
              />
              <label className="text-sm font-medium">Resume (URL)</label>
              <input
                type="url"
                value={person.resumeUrl || ''}
                onChange={(e) => {
                  const newPersonnel = [...(formData.keyPersonnel || [])];
                  newPersonnel[index] = { ...person, resumeUrl: e.target.value };
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                placeholder="https://"
                className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary mb-2"
              />
              <button
                type="button"
                onClick={() => {
                  const newPersonnel = (formData.keyPersonnel || []).filter((_, i) => i !== index);
                  handleFieldChange('keyPersonnel', newPersonnel);
                }}
                className="mt-1 p-1 text-urgent hover:text-urgent rounded"
              >
                <Minus className="h-4 w-4" /> Remove Person
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => {
              const newPersonnel = [...(formData.keyPersonnel || []), { name: '', title: '', email: '', phone: '', linkedinUrl: '', resumeUrl: '' }];
              handleFieldChange('keyPersonnel', newPersonnel);
            }}
            className="flex items-center text-bw-primary hover:text-bw-primary text-sm"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Person
          </button>
        </div>
      );
    }

    // Handle arrays for other fields (e.g., coreCompetencies, naicsCodes)
    if (Array.isArray(value)) {
      return (
        <div className="space-y-2">
          {value.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => handleArrayItemChange(key, index, e.target.value)}
                className="flex-1 rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
              />
              <button
                type="button"
                onClick={() => handleRemoveArrayItem(key, index)}
                className="p-1 text-urgent hover:text-urgent rounded-full hover:bg-urgent-bg"
              >
                <Minus className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => handleAddArrayItem(key)}
            className="flex items-center text-bw-primary hover:text-bw-primary text-sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Item
          </button>
        </div>
      );
    }

    // For narrative fields (mission statement and executive summary), use textarea with minimum 6 lines
    if (key === 'missionStatement' || key === 'executiveSummary') {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => handleFieldChange(key, e.target.value)}
          className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
          rows={6}
          style={{ minHeight: '150px' }}
          placeholder={`Enter your ${key === 'missionStatement' ? 'mission statement' : 'executive summary'}...`}
        />
      );
    }

    // For objects not specifically handled, display as formatted JSON.
    if (typeof value === 'object' && !React.isValidElement(value)) {
      return (
        <pre className="whitespace-pre-wrap text-sm bg-bw-surface-subtle p-2 rounded">
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    // Otherwise, render a simple text input.
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => handleFieldChange(key, e.target.value)}
        className="w-full rounded-md border-bw-border shadow-sm focus:border-blue-500 focus:ring-bw-primary"
      />
    );
  };

  const handleApplyData = () => {
    const updates: Partial<ExtractedData> = {};
    Object.keys(formData).forEach(key => {
      if (selectedFields.has(key)) {
        updates[key as keyof ExtractedData] = formData[key as keyof ExtractedData];
      }
    });
    try {
      onSave(updates);
      toast.success('Profile data updated successfully');
    } catch (error) {
      console.error('Error saving profile data:', error);
      toast.error('Failed to update profile data');
    }
  };

  return (
    <div className="bg-bw-surface rounded-lg shadow-lg p-6">
      {/* Explanation Area */}
      <div className="bg-bw-primary-tint border border-bw-primary/20 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-bw-primary mt-0.5 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-bw-primary">Review Extracted Data</h3>
            <p className="mt-1 text-sm text-bw-primary">
              Review the automatically extracted data below. Select the fields you want to import and edit any values that need correction.
              Fields with higher confidence scores are more likely to be accurate.
            </p>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {formData.warnings.length > 0 && (
        <div className="mb-6 bg-fit-moderate-bg border border-fit-moderate-border rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-fit-moderate mt-0.5 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-fit-moderate">Please Review These Warnings</h3>
              <ul className="mt-2 text-sm text-fit-moderate space-y-1">
                {formData.warnings.map((warning, index) => (
                  <li key={index}>• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Data Sections */}
      {Object.entries(sections).map(([sectionTitle, keys]) => (
        <div key={sectionTitle} className="mb-8">
          <div className="flex items-center justify-between border-b pb-2 mb-4">
            <h2 className="text-xl font-semibold text-bw-ink">{sectionTitle}</h2>
            <button
              type="button"
              onClick={() => toggleSectionSelection(keys)}
              className="flex items-center text-sm text-bw-muted hover:text-bw-ink"
            >
              {keys.every(key => selectedFields.has(key as string)) ? (
                <>
                  <CheckSquare className="h-4 w-4 mr-1" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="h-4 w-4 mr-1" />
                  Select All
                </>
              )}
            </button>
          </div>
          <div className="space-y-6">
            {keys.map((key) => {
              const field = fieldLabels[key];
              if (!field) return null;
              return (
                <div key={key as string} className="relative group">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedFields.has(key as string)}
                      onChange={() => toggleSectionSelection([key])}
                      className="mt-1 h-4 w-4 text-bw-primary border-bw-border rounded focus:ring-bw-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <label className="text-sm font-medium text-bw-ink">
                          {field.label}
                        </label>
                        {renderConfidence(key)}
                        <div className="relative ml-2 group">
                          <Info className="h-4 w-4 text-bw-muted cursor-help" />
                          <div className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                            {field.description}
                          </div>
                        </div>
                      </div>
                      <div className="mt-1">
                        {formData[key] !== undefined ? (
                          <div className="relative group">
                            {renderEditableValue(key, formData[key])}
                            <button
                              type="button"
                              onClick={() => setEditingField(key as string)}
                              className="absolute top-2 right-2 p-1 text-bw-muted hover:text-bw-muted opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <em className="text-bw-muted">No data extracted</em>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Form Actions */}
      <div className="flex justify-end space-x-4 pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-bw-border rounded-md text-bw-ink hover:bg-bw-surface-subtle focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bw-primary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleApplyData}
          className="px-4 py-2 bg-bw-primary text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-bw-primary flex items-center"
        >
          <Save className="h-5 w-5 mr-2" />
          Apply Selected Data
        </button>
      </div>
    </div>
  );
}